/* eslint-disable jsx-a11y/media-has-caption */
import styles from "./VideoPlayer.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { useCallback, useEffect, useRef, useState } from "react";
import wrapperHOC from "@Utils/wrapperHOC";

interface Props {
  name: string;
  videoUrl: string;
  posterUrl?: string;
  autoplay?: boolean;
  isDesktop?: boolean;
  centerAlign?: boolean;
  inLoop?: boolean;
  noControls?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  hasClickOverlay?: boolean;
  overlayClassName?: string;
  isYoutube?: boolean;
  containerClassName?: string;
  videoPlayerClassName?: string;
  handleOnVideoEnd?: () => void;
  forceAutoPlay?: boolean;
  showCustomControls?: boolean;
  onPlaying?: (playing: boolean) => void;
  customRef?: any;
  feedback?: boolean;
  currentRef?: (vid: any) => void;
  width?: number | string;
  height?: number | string;
  isLazyLoaded?: boolean;
  onOverlayClick?: (e: any) => void;
  controlsList?: string;
  id?: string;
}
const VideoPlayer: React.FC<Props> = (props: Props) => {
  const {
    name,
    videoUrl,
    posterUrl,
    autoplay,
    isDesktop,
    centerAlign,
    inLoop,
    noControls,
    playsInline,
    muted,
    hasClickOverlay,
    overlayClassName,
    containerClassName,
    videoPlayerClassName,
    handleOnVideoEnd,
    forceAutoPlay,
    showCustomControls,
    onPlaying,
    customRef,
    currentRef,
    width,
    height,
    isLazyLoaded,
    onOverlayClick,
    controlsList,
    id,
  } = props;

  const videoRef = useRef<any>(null);
  const pauseTimerRef = useRef<any>(null);
  const [videoPromiseState, setVideoPromiseState] = useState<any>(null);
  const handlePlay = (): void => {
    const videoPlay = new CustomEvent("videoPlay", { detail: { name } });
    window.dispatchEvent(videoPlay);
  };

  const handleCheckOtherVideoPlay = (event: any): void => {
    if (event.detail.name !== name && videoRef.current) {
      if (videoPromiseState !== undefined && videoPromiseState !== null) {
        videoPromiseState
          .then(() => {
            videoRef.current?.pause();
          })
          .catch((error: any) => {
            console.log(error);
          });
      } else {
        videoRef.current?.pause();
      }
    }
  };

  const handleOnReady = () => {
    setVideoPromiseState(videoRef.current?.play());
  };

  const handleonEnd = () => {
    handleOnVideoEnd?.();
    if (showCustomControls) {
      onPlaying?.(false);
    }
  };

  const getRef = (ref: any) => {
    videoRef.current = ref;
    currentRef?.(videoRef.current);
    if (customRef) {
      customRef.current = ref;
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener("play", handlePlay);
      window.addEventListener("videoPlay", handleCheckOtherVideoPlay);
      videoRef.current.addEventListener("ended", handleonEnd, false);
      if (forceAutoPlay) {
        videoRef.current.addEventListener("canplay", handleOnReady);
      }
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener("play", handlePlay);
        window.removeEventListener("videoPlay", handleCheckOtherVideoPlay);
        videoRef.current.removeEventListener("ended", handleonEnd, false);
        if (forceAutoPlay) {
          videoRef.current.removeEventListener("canplay", handleOnReady);
        }
      }
    };
  }, [videoRef, handlePlay, handleCheckOtherVideoPlay]);

  const renderVideoPlayer = useCallback(() => {
    const config = {
      muted,
      autoPlay: autoplay && !forceAutoPlay,
    };
    return (
      <video
        ref={getRef}
        loop={inLoop}
        playsInline={playsInline}
        controls={!noControls}
        controlsList={controlsList}
        preload={isLazyLoaded ? "none" : "auto"}
        height={height}
        width={width || "100%"}
        onPause={() => {
          pauseTimerRef.current = setTimeout(() => {
            onPlaying?.(false);
          }, 100);
        }}
        onPlay={() => {
          clearTimeout(pauseTimerRef.current);
          onPlaying?.(true);
        }}
        onSeeking={() => {
          clearTimeout(pauseTimerRef.current);
          onPlaying?.(true);
        }}
        onSeeked={() => {
          if (!videoRef.current?.paused) {
            onPlaying?.(true);
          }
        }}
        poster={posterUrl || ""}
        className={formatClassNames(styles.videoPlayer, videoPlayerClassName)}
        src={`${videoUrl}${posterUrl ? "" : "#t=0.01"}`}
        id={id}
        {...config}
      />
    );
  }, [
    muted,
    autoplay,
    forceAutoPlay,
    inLoop,
    playsInline,
    noControls,
    controlsList,
    isLazyLoaded,
    height,
    width,
    posterUrl,
    videoUrl,
    id,
  ]);

  return (
    <div
      className={formatClassNames(
        styles.videoContainer,
        isDesktop && styles.desktopVideoContainer,
        showCustomControls && styles.noFixedHeight,
        centerAlign && styles.centerAlign,
        containerClassName,
      )}
    >
      {hasClickOverlay && (
        <div
          id={overlayClassName}
          role="none"
          className={styles.videoCover}
          onClick={onOverlayClick}
        >
          &nbsp;
        </div>
      )}
      {renderVideoPlayer()}
    </div>
  );
};

VideoPlayer.defaultProps = {
  posterUrl: "",
  autoplay: false,
  isDesktop: false,
  centerAlign: false,
  inLoop: false,
  noControls: false,
  muted: false,
  playsInline: false,
  hasClickOverlay: false,
  overlayClassName: "",
  isYoutube: false,
  containerClassName: "",
  videoPlayerClassName: "",
  handleOnVideoEnd: () => null,
  forceAutoPlay: false,
  showCustomControls: false,
  onPlaying: () => null,
  customRef: null,
  feedback: false,
  currentRef: () => null,
  width: "",
  height: "",
  isLazyLoaded: false,
  onOverlayClick: () => null,
  controlsList: "",
  id: "",
};

export default wrapperHOC(VideoPlayer, {
  componentName: "VideoPlayer-Global",
  showForChina: true,
});
